import React from 'react';
import { Box, Typography, Autocomplete, TextField, Chip } from '@mui/material';
import { Category, Work } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../../app/hooks';
import { updateTeamCategories } from '../../../state';
import { useGetTicketCategoriesQuery } from '../../../../../services/DimensionService/dimensionApi';

/**
 * TeamCategorySelection - Component for managing which categories a team is responsible for
 */
const TeamCategorySelection: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(state => state.team.currentTeam);
  
  // Fetch available ticket categories from the backend
  const { data: availableCategories = [], isLoading, error } = useGetTicketCategoriesQuery();
  
  const selectedCategories = currentTeam?.assignedCategories || [];

  const handleCategoryChange = (_: any, newValue: string[]) => {
    dispatch(updateTeamCategories(newValue));
  };

  if (isLoading) return <Typography>Loading categories...</Typography>;
  if (error) return <Typography color="error">Error loading categories</Typography>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Work />
        <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
          Ticket Categories
        </Typography>
      </Box>

      <Autocomplete
        multiple
        size="small"
        options={availableCategories.map(cat => cat.value)}
        value={selectedCategories}
        onChange={handleCategoryChange}
        getOptionLabel={(option) => {
          const category = availableCategories.find(cat => cat.value === option);
          return category?.name || option;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Type to search and select categories"
            placeholder={selectedCategories.length === 0 ? "Start typing category name..." : "Add more categories..."}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const category = availableCategories.find(cat => cat.value === option);
            return (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={category?.name || option}
                size="small"
                icon={<Category />}
                sx={{
                  backgroundColor: '#e0f2fe',
                  color: '#0277bd',
                  '& .MuiChip-deleteIcon': {
                    color: '#0277bd',
                    ':hover': {
                      color: '#01579b'
                    }
                  }
                }}
              />
            );
          })
        }
        renderOption={(props, option) => {
          const category = availableCategories.find(cat => cat.value === option);
          return (
            <Box component="li" {...props}>
              <Box display="flex" alignItems="center" gap={1}>
                <Category fontSize="small" />
                <Box>
                  <Typography variant="body2">{category?.name || option}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          return options.filter(option => {
            const category = availableCategories.find(cat => cat.value === option);
            const name = category?.name || option;
            return name.toLowerCase().includes(inputValue.toLowerCase()) ||
                   option.toLowerCase().includes(inputValue.toLowerCase());
          });
        }}
        noOptionsText="No categories found"
      />

      {selectedCategories.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            This team will be responsible for {selectedCategories.length} categor{selectedCategories.length !== 1 ? 'ies' : 'y'}.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TeamCategorySelection;